import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DocumentChartBarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { INBOUND_RECEIVING_PUTAWAY } from '../../consts/reports';
import { CHILDREN_TAB_INBOUND_RECEIVING_PUTAWAY, isAdminRole } from '../../consts/management/users/users';
import { formatNumber } from '../warehouse/format';
import {
  cleanupEventLabel,
  cleanupItemSummary,
  formatInboundDateTime,
  movementTypeLabel,
  resolutionReasonLabel,
} from './inboundReceivingPutawayFormat';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const kinds = [
  ['unconfirmed_shipped_qty', 'Unconfirmed shipped quantity'],
  ['manual_move_no_transfer', 'Moved without transfer'],
  ['unverified_destination', 'Moved to unmapped/inactive location'],
  ['supplier_short_ship', 'Supplier short-ship'],
  ['missing_delivery_scan', 'No UPS delivery scan'],
  ['receipt_source_mismatch', 'Eadin vs ShipHero count'],
];
const emptyReport = {
  receiveOverdue: [], putAwayOverdue: [], cleanupItems: [], cleanupCounts: { open: 0, byKind: {} },
  summary: { receive: {}, putAway: {} }, cleanupCandidates: [], dataWarnings: [],
};
const labelFor = (kind) => kinds.find(([value]) => value === kind)?.[1] || 'Other data check';
const moneyless = (value) => formatNumber(Number(value || 0));

const EvidenceTable = ({ title, headers, rows }) => (
  <section className="mt-4">
    <h3 className="mb-2 font-medium">{title}</h3>
    {rows.length ? <div className="overflow-x-auto rounded border border-gray-200">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
          {headers.map((header) => <th key={header} className="whitespace-nowrap px-3 py-2">{header}</th>)}
        </tr></thead>
        <tbody>{rows.map((row, rowIndex) => <tr key={`${title}-${rowIndex}`} className="border-t">
          {row.map((value, columnIndex) => <td key={`${title}-${rowIndex}-${columnIndex}`} className="px-3 py-2 align-top">{value || '—'}</td>)}
        </tr>)}</tbody>
      </table>
    </div> : <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">None</p>}
  </section>
);

const InboundReceivingPutawayReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { currentUser } = useSelector((state) => state.auth);
  const canEdit = isAdminRole(currentUser?.role) || (currentUser?.permissions || []).some((group) => {
    const [, children] = Object.entries(group || {})[0] || [];
    return (children || []).some((child) => child[CHILDREN_TAB_INBOUND_RECEIVING_PUTAWAY] === 'edit');
  });
  const tab = params.get('tab') || 'action';
  const status = params.get('status') || 'open';
  const kind = params.get('kind') || '';
  const selectedId = params.get('item') || '';
  const [report, setReport] = useState(emptyReport);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState('');
  const [reason, setReason] = useState('verified_location_quantity');
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState('');
  const [verified, setVerified] = useState([{ location_code: '', quantity: '' }]);
  const [reopenNote, setReopenNote] = useState('');

  const updateParams = (next) => {
    const values = { ...Object.fromEntries(params.entries()), ...next };
    Object.keys(values).forEach((key) => { if (!values[key]) delete values[key]; });
    setParams(values);
  };
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({ status, ...(kind ? { kind } : {}) });
      const response = await fetch(`/api/v1/reports/inbound-receiving-putaway?${query}`, { headers: authHeaders() });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to load the inbound report');
      setReport(body);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [status, kind]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    try {
      const response = await fetch(`/api/v1/reports/inbound-receiving-putaway/cleanup/${selectedId}`, { headers: authHeaders() });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to load cleanup evidence');
      setDetail(body);
    } catch (requestError) {
      setActionError(requestError.message);
    }
  }, [selectedId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const downloadPdf = async () => {
    setWorking(true);
    setActionError('');
    try {
      const response = await fetch('/api/v1/reports/inbound-receiving-putaway/export?format=pdf', { headers: authHeaders() });
      if (!response.ok) throw new Error('Unable to download the action sheet');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'inbound-receiving-putaway-action-sheet.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const refresh = async () => {
    setWorking(true);
    setActionError('');
    try {
      const response = await fetch('/api/v1/reports/inbound-receiving-putaway/cleanup/refresh', {
        method: 'POST', headers: authHeaders(),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to refresh cleanup list');
      setReport(body);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const submitResolution = async (event) => {
    event.preventDefault();
    setWorking(true);
    setActionError('');
    try {
      const response = await fetch(`/api/v1/reports/inbound-receiving-putaway/cleanup/${selectedId}/resolve`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason, note, evidence,
          verifiedLocations: reason === 'verified_location_quantity'
            ? verified.map((row) => ({ location_code: row.location_code, quantity: Number(row.quantity) }))
            : [],
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to resolve cleanup item');
      await load();
      await loadDetail();
      setNote('');
      setEvidence('');
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const reopen = async () => {
    setWorking(true);
    setActionError('');
    try {
      const response = await fetch(`/api/v1/reports/inbound-receiving-putaway/cleanup/${selectedId}/reopen`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: reopenNote }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to reopen cleanup item');
      setReopenNote('');
      await load();
      await loadDetail();
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setWorking(false);
    }
  };

  const tabButton = (value, text) => (
    <button type="button" onClick={() => updateParams({ tab: value, item: '' })}
      className={`border-b-2 px-3 py-2 text-sm font-medium ${tab === value ? 'border-black text-black' : 'border-transparent text-gray-500'}`}>
      {text}
    </button>
  );
  const sectionTable = (title, rows, action) => (
    <section className="overflow-x-auto rounded border border-gray-200">
      <h2 className="px-3 py-2 text-base font-semibold">{title}</h2>
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
          {['PO', 'Delivered (PT)', 'Units left', 'Late by', 'SKUs', 'To do'].map((name) => <th key={name} className="px-3 py-2">{name}</th>)}
        </tr></thead>
        <tbody>{rows.length ? rows.map((row, index) => (
          <tr key={`${row.po_number}-${row.tracking}-${index}`} className="border-t">
            <td className="px-3 py-2 font-medium">{row.po_number}</td>
            <td className="px-3 py-2">{formatInboundDateTime(row.delivered_at, 'Unknown')}<div className="text-xs text-gray-500">UPS …{String(row.tracking || '').slice(-6)}</div></td>
            <td className="px-3 py-2">{moneyless(row.units)}</td>
            <td className="px-3 py-2">{Math.floor((row.late_by_minutes || 0) / 1440)}d {Math.floor(((row.late_by_minutes || 0) % 1440) / 60)}h</td>
            <td className="px-3 py-2">{(row.lines || []).map((line) => `${line.sku} ${moneyless(line.units)}`).join(' · ')}</td>
            <td className="px-3 py-2">{action}</td>
          </tr>
        )) : <tr><td colSpan="6" className="px-3 py-5 text-center text-gray-500">Nothing confirmed overdue.</td></tr>}</tbody>
      </table>
    </section>
  );

  const item = detail?.item;
  const verifiedTotal = verified.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  const itemEvidence = item?.evidence || {};
  const evidenceRows = detail?.evidenceRows || {};
  const receivedAtByBox = new Map();
  (evidenceRows.receivedLogs || []).forEach((log) => {
    const boxId = String(log.box_id || '');
    const previous = receivedAtByBox.get(boxId);
    if (boxId && log.at && (!previous || new Date(log.at) > new Date(previous))) {
      receivedAtByBox.set(boxId, log.at);
    }
  });
  const recordRows = [
    ['Planned', moneyless(itemEvidence.planned)],
    ['Shipped (confirmed yes/no)', `${moneyless(itemEvidence.shipped)} · ${itemEvidence.ship_confirmed ? 'Yes' : 'No'}`],
    ['Received in Eadin', moneyless(itemEvidence.eadin_received)],
    ['Received in ShipHero', moneyless(itemEvidence.shiphero_received)],
    ['Put away by transfer', Number(itemEvidence.put_away_inferred || 0) > 0
      ? `${moneyless(itemEvidence.put_away)} (${moneyless(itemEvidence.put_away_inferred)} matched by order, not a direct receipt link)`
      : moneyless(itemEvidence.put_away)],
    ['Still in Receiving', moneyless(itemEvidence.in_receiving)],
  ];
  const boxRows = (evidenceRows.boxes || []).map((box) => [
    box.box_id ? `#${box.box_id}` : 'Unknown',
    box.trk || 'Unknown',
    moneyless(box.qty),
    box.shipped == null ? 'Unknown' : moneyless(box.shipped),
    box.rec == null ? 'Unknown' : moneyless(box.rec),
    formatInboundDateTime(box.act || receivedAtByBox.get(String(box.box_id || '')), 'Unknown'),
  ]);
  const movementRows = (evidenceRows.movements || []).map((movement) => {
    const change = Number(movement.chg || 0);
    return [
      formatInboundDateTime(movement.at, 'Unknown'),
      movement.loc || 'Unknown',
      `${change > 0 ? '+' : ''}${moneyless(change)}`,
      movementTypeLabel(movement.ct),
      movement.reason || '—',
    ];
  });
  const receivingLogRows = (evidenceRows.receivedLogs || []).map((log) => [
    log.id == null ? 'Unknown' : `#${log.id}`,
    formatInboundDateTime(log.at, 'Unknown'),
    log.amt == null ? 'Unknown' : moneyless(log.amt),
    log.received === true ? 'Yes' : log.received === false ? 'No' : 'Unknown',
  ]);
  const resolutionEvidence = item?.resolution_evidence || {};
  const verifiedLocations = Array.isArray(resolutionEvidence.locations) ? resolutionEvidence.locations : [];
  const resolutionEvidenceText = resolutionEvidence.checked
    || (resolutionEvidence.source === 'inbound refresh' ? 'Automatically reconciled against the inbound refresh.' : 'None');
  const canResolve = Boolean(note.trim().length >= 10 && evidence.trim() && (
    reason !== 'verified_location_quantity'
    || (verified.length && verified.every((row) => row.location_code && Number.isInteger(Number(row.quantity)) && Number(row.quantity) > 0)
      && verifiedTotal === Number(item?.units))
  ));
  const exportButton = <button type="button" onClick={downloadPdf} disabled={working} className="rounded bg-black px-3 py-2 text-sm text-white">Download PDF</button>;

  return (
    <ReportTemplate
      reportId={INBOUND_RECEIVING_PUTAWAY}
      title="Inbound Receiving & Put-away"
      description="Receiving and put-away SLAs, with evidence-based follow-up for inbound data cleanup."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      exportButton={exportButton}
      renderChildrenOnly
    >
      <div className="space-y-4">
        <nav className="flex border-b border-gray-200">
          {tabButton('action', 'Action sheet')}
          {tabButton('cleanup', `Cleanup list (${formatNumber(report.cleanupCounts?.open || 0)} open)`)}
        </nav>
        {actionError && <p role="alert" className="text-sm text-red-700">{actionError}</p>}
        {tab === 'action' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Las Vegas warehouse · as of {formatInboundDateTime(report.asOf)} PT · oldest first · receive within 24h, put away within 48h, weekends skipped</p>
            {sectionTable(`Receive overdue · ${report.summary?.receive?.units || 0} units`, report.receiveOverdue || [], 'Count and receive the remaining units.')}
            {sectionTable(`Put away overdue · ${report.summary?.putAway?.units || 0} units still in Receiving`, report.putAwayOverdue || [], 'Transfer to storage location.')}
            {report.dataCheck && <p className="rounded border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">
              {report.dataCheck.text}{' '}
              <button type="button" className="underline" onClick={() => updateParams({ tab: 'cleanup' })}>{report.dataCheck.linkText}</button>
            </p>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {['open', 'resolved', 'all'].map((value) => <button key={value} type="button" onClick={() => updateParams({ status: value })}
                className={`rounded border px-3 py-1 text-sm ${status === value ? 'bg-gray-900 text-white' : 'bg-white'}`}>{value[0].toUpperCase() + value.slice(1)}</button>)}
              {canEdit && <button type="button" onClick={refresh} disabled={working} className="ml-auto rounded border px-3 py-1 text-sm">Refresh cleanup list</button>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => updateParams({ kind: '' })} className={`rounded-full border px-3 py-1 text-xs ${!kind ? 'bg-gray-900 text-white' : ''}`}>All kinds</button>
              {kinds.map(([value, label]) => <button key={value} type="button" onClick={() => updateParams({ kind: kind === value ? '' : value })}
                className={`rounded-full border px-3 py-1 text-xs ${kind === value ? 'bg-gray-900 text-white' : ''}`}>{label} ({report.cleanupCounts?.byKind?.[value]?.open || 0})</button>)}
            </div>
            <div className="overflow-x-auto rounded border">
              <table className="min-w-full text-left text-sm"><thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr>
                {['PO', 'Tracking', 'Delivered (PT)', 'SKU', 'Units', 'Kind', 'First seen (PT)', 'Status'].map((name) => <th key={name} className="px-3 py-2">{name}</th>)}
              </tr></thead><tbody>{report.cleanupItems?.length ? report.cleanupItems.map((row) => <tr key={row.id} onClick={() => updateParams({ item: row.id })}
                className="cursor-pointer border-t hover:bg-gray-50"><td className="px-3 py-2">{row.po_number}</td><td className="px-3 py-2">{row.tracking || '—'}</td>
                <td className="px-3 py-2">{formatInboundDateTime(row.delivered_at, 'Unknown')}</td><td className="px-3 py-2">{row.sku}</td>
                <td className="px-3 py-2">{moneyless(row.units)}</td><td className="px-3 py-2">{labelFor(row.kind)}</td>
                <td className="px-3 py-2">{formatInboundDateTime(row.first_seen_at)}</td><td className="px-3 py-2">{row.status === 'resolved' ? 'Resolved' : 'Open'}{!row.present_in_latest_run && row.status === 'open' ? ' · No longer detected — verify' : ''}</td></tr>)
                : <tr><td colSpan="8" className="px-3 py-5 text-center text-gray-500">No cleanup items match this filter.</td></tr>}</tbody></table>
            </div>
          </div>
        )}
        {item && (
          <div className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true">
            <div className="h-full max-h-screen min-h-0 w-full max-w-2xl overflow-y-auto overscroll-contain bg-white p-5 shadow-xl">
              <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">{item.po_number} · {item.sku}</h2>
                <button type="button" aria-label="Close cleanup item" onClick={() => updateParams({ item: '' })}><XMarkIcon className="h-5 w-5" /></button></div>
              <p className="mt-2 text-sm text-gray-700">{cleanupItemSummary(item)}</p>
              <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <span className={`rounded-full px-2 py-0.5 font-medium ${item.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                  {item.status === 'resolved' ? 'Resolved' : 'Open'}
                </span>
                <span>{item.present_in_latest_run ? 'Present in the latest refresh' : 'No longer detected — verify'}</span>
              </p>

              <EvidenceTable title="What the records show" headers={['Measure', 'Quantity / status']} rows={recordRows} />
              <EvidenceTable title="Boxes" headers={['Box', 'Tracking', 'Planned', 'Shipped', 'Received', 'Received at PT']} rows={boxRows} />
              <EvidenceTable title="Movements" headers={['Date PT', 'Location', 'Change', 'Type', 'Reason']} rows={movementRows} />
              <EvidenceTable title="Receiving log" headers={['Log', 'Date PT', 'Quantity', 'Received']} rows={receivingLogRows} />

              <div className="my-4 flex gap-4 text-sm">
                {detail.links?.purchaseOrder && <a className="underline" href={detail.links.purchaseOrder}>Purchase order</a>}
                {detail.links?.transfers && <a className="underline" href={detail.links.transfers}>Location Transfers</a>}
              </div>

              {item.status === 'resolved' ? <section id="cleanup-resolution" className="mt-4 rounded border border-green-200 bg-green-50 p-3 text-sm">
                <h3 className="font-medium">Resolution</h3>
                <p className="mt-2">Resolved by {item.resolver?.full_name || item.resolver?.email || 'System'} · {formatInboundDateTime(item.resolved_at)} PT</p>
                <p className="mt-1"><b>Reason:</b> {resolutionReasonLabel(item.resolution_reason)}</p>
                <p className="mt-1"><b>Note:</b> {item.resolution_note || 'None'}</p>
                <p className="mt-1"><b>Evidence:</b> {resolutionEvidenceText}</p>
                <div className="mt-1">
                  <b>Verified locations:</b>
                  {verifiedLocations.length ? <ul className="ml-5 list-disc">
                    {verifiedLocations.map((location, index) => <li key={`${location.location_code}-${index}`}>{location.location_code}: {moneyless(location.quantity)} units</li>)}
                  </ul> : <span> None</span>}
                </div>
                {canEdit && <form onSubmit={(event) => { event.preventDefault(); reopen(); }} className="mt-3 flex flex-wrap gap-2">
                  <input aria-label="Reopen note" value={reopenNote} onChange={(event) => setReopenNote(event.target.value)} className="min-w-0 flex-1 rounded border px-2 py-1" />
                  <button type="submit" disabled={working || reopenNote.trim().length < 10} className="rounded border px-3 py-1">Reopen</button>
                </form>}
              </section> : canEdit && <form id="cleanup-resolution-form" onSubmit={submitResolution} className="mt-4 space-y-3 border-t pt-4">
                <h3 className="font-medium">Resolve item</h3>
                <p className="text-sm text-gray-600">Resolving only records your verification. It does not change inventory, receipts or transfers.</p>
                <label className="block text-sm">Reason<select value={reason} onChange={(event) => setReason(event.target.value)} className="mt-1 block rounded border p-2">
                  <option value="verified_location_quantity">Verified location quantity</option><option value="source_record_corrected">Source record corrected</option>
                  {(item.kind === 'supplier_short_ship' || item.kind === 'unconfirmed_shipped_qty') && <option value="supplier_short_confirmed">Supplier short confirmed</option>}
                </select></label>
                {reason === 'verified_location_quantity' && <div className="space-y-2">
                  {verified.map((row, index) => <div key={index} className="flex gap-2"><input placeholder="Location code" value={row.location_code} onChange={(event) => setVerified(verified.map((entry, i) => i === index ? { ...entry, location_code: event.target.value } : entry))} className="min-w-0 flex-1 rounded border p-2" />
                    <input type="number" min="1" placeholder="Quantity" value={row.quantity} onChange={(event) => setVerified(verified.map((entry, i) => i === index ? { ...entry, quantity: event.target.value } : entry))} className="w-28 rounded border p-2" /></div>)}
                  <button type="button" onClick={() => setVerified([...verified, { location_code: '', quantity: '' }])} className="text-sm underline">Add location row</button>
                  <p className="text-xs text-gray-500">Verified total: {moneyless(verifiedTotal)} / {moneyless(item.units)}</p>
                </div>}
                <label className="block text-sm">Note<textarea value={note} onChange={(event) => setNote(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
                <label className="block text-sm">Evidence<textarea value={evidence} onChange={(event) => setEvidence(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
                <button type="submit" disabled={working || !canResolve} className="rounded bg-black px-3 py-2 text-sm text-white disabled:opacity-40">Resolve item</button>
              </form>}

              <section className="mt-5 border-t pt-4">
                <h3 className="font-medium">History</h3>
                {(item.events || []).length ? <ol className="mt-3 space-y-3 text-sm">{(item.events || []).map((event) => <li key={event.id} className="border-l-2 border-gray-300 pl-3">
                  <p><b>{cleanupEventLabel(event.action)}</b> · {event.user?.full_name || event.user?.email || 'System'} · {formatInboundDateTime(event.created_at)} PT</p>
                  {(event.action === 'auto_reopened' ? 'Source evidence changed after the previous resolution.' : event.note) && <p className="mt-1 text-gray-600">
                    {event.action === 'auto_reopened' ? 'Source evidence changed after the previous resolution.' : event.note}
                  </p>}
                </li>)}</ol> : <p className="mt-2 text-sm text-gray-500">None</p>}
              </section>
              {actionError && <p role="alert" className="mt-3 text-sm text-red-700">{actionError}</p>}
            </div>
          </div>
        )}
      </div>
    </ReportTemplate>
  );
};

export default InboundReceivingPutawayReport;
